import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Check, UserPlus } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { extractError } from '../../lib/types.ts';
import { Modal } from '../ui/Modal.tsx';
import { Badge } from '../ui/Badge.tsx';
import { useToast } from '../ui/Toast.tsx';

interface Props {
  segmentId: string;
  open: boolean;
  onClose: () => void;
  existingMemberIds: Set<string>;
}

interface SearchResult {
  id: string;
  firstName: string | null;
  lastName: string | null;
  status: string;
  email: string | null;
}

export function AddSegmentMemberModal({ segmentId, open, onClose, existingMemberIds }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setDebouncedQuery('');
      setAddedIds(new Set());
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const { data, isLoading } = useQuery<{ items: SearchResult[]; total: number }>({
    queryKey: ['customer-search', debouncedQuery],
    queryFn: () => api.get(`/api/v1/customers/search?q=${encodeURIComponent(debouncedQuery)}`).then((r) => r.data),
    enabled: open && debouncedQuery.length >= 2,
  });

  const addMutation = useMutation({
    mutationFn: (customerId: string) =>
      api.post(`/api/v1/segments/${segmentId}/members`, { customerId }),
    onSuccess: (_data, customerId) => {
      setAddedIds((prev) => new Set(prev).add(customerId));
      qc.invalidateQueries({ queryKey: ['segment-members', segmentId] });
      qc.invalidateQueries({ queryKey: ['segment', segmentId] });
      toast.success('Member added');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to add member')),
  });

  const results = data?.items ?? [];

  const getStatus = (id: string): 'existing' | 'added' | 'available' => {
    if (existingMemberIds.has(id)) return 'existing';
    if (addedIds.has(id)) return 'added';
    return 'available';
  };

  return (
    <Modal open={open} onClose={onClose} title="Add members" description="Search for customers to add to this segment.">
      {/* Search input */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full pl-9 pr-3 py-2.5 text-sm border border-zinc-200 rounded-lg bg-zinc-50 focus:bg-white focus:border-zinc-400 focus:outline-none transition-colors"
        />
      </div>

      {/* Results */}
      <div className="mt-3 max-h-72 overflow-y-auto -mx-1">
        {debouncedQuery.length < 2 ? (
          <div className="py-8 text-center">
            <UserPlus size={22} className="text-zinc-300 mx-auto mb-2" />
            <p className="text-xs text-zinc-400">Type at least 2 characters to search</p>
          </div>
        ) : isLoading ? (
          <div className="py-8 text-center">
            <p className="text-xs text-zinc-400 animate-pulse">Searching…</p>
          </div>
        ) : results.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-xs text-zinc-400">No customers found for "{debouncedQuery}"</p>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {results.map((c) => {
              const status = getStatus(c.id);
              const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
              return (
                <li
                  key={c.id}
                  className="flex items-center justify-between gap-3 px-1 py-2.5 hover:bg-zinc-50 rounded-lg transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-800 truncate">{name}</p>
                    <p className="text-xs text-zinc-400 truncate">{c.email ?? 'No email'}</p>
                  </div>
                  <Badge variant={c.status === 'active' ? 'success' : 'default'} className="shrink-0">
                    {c.status}
                  </Badge>
                  {status === 'existing' ? (
                    <span className="flex items-center gap-1 text-[11px] text-zinc-400 shrink-0">
                      <Check size={12} /> Member
                    </span>
                  ) : status === 'added' ? (
                    <span className="flex items-center gap-1 text-[11px] text-emerald-500 shrink-0">
                      <Check size={12} /> Added
                    </span>
                  ) : (
                    <button
                      onClick={() => addMutation.mutate(c.id)}
                      disabled={addMutation.isPending}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-zinc-700 hover:text-zinc-900 bg-zinc-100 hover:bg-zinc-200 rounded-md transition-colors shrink-0 disabled:opacity-50"
                    >
                      <Plus size={11} /> Add
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {data && data.total > 20 && (
        <p className="text-[11px] text-zinc-400 mt-2 text-center">
          Showing 20 of {data.total} results — refine your search for more specific matches.
        </p>
      )}
    </Modal>
  );
}
